import { useTranslation } from 'react-i18next';
import { DayRing } from '../components/DayRing.js';

/**
 * «Сегодня» — хаб: кольцо еды + полосы Б/Ж/У, активность, вес, привычки.
 * Этап 0: мок-данные; каждый блок — будущая дверь в свой раздел.
 * Все строки (включая мок-подписи) идут через i18n — экран целиком
 * рендерится на языке пользователя, без вкраплений хардкода.
 */

// Мок дня: только числа и ключи словаря. Реальные данные — /v1/feed и
// /v1/plan на этапе 1.
const MOCK = {
  plan: { kcal: 2100, proteinG: 136, fatG: 72, carbsG: 227 },
  consumed: { kcal: 1290, proteinG: 82, fatG: 46, carbsG: 141 },
  workout: { titleKey: 'today.mockWorkoutTitle', durationMin: 45, kcal: 320 },
  steps: 6540,
  stepsGoal: 8000,
  weightKg: 82.4,
  weightDelta: -0.3,
  habits: [
    { nameKey: 'today.habitWater', value: '5 / 8', done: false },
    { nameKey: 'today.habitSteps', valueKey: 'today.habitStepsValue', done: false },
    { nameKey: 'today.habitWeigh', value: '✓', done: true },
    { nameKey: 'today.habitNoSugar', value: '✓', done: true },
  ],
} as const;

interface MacroRowProps {
  label: string;
  current: number;
  target: number;
  colorVar: string;
}

function MacroRow({ label, current, target, colorVar }: MacroRowProps): JSX.Element {
  const { t } = useTranslation();
  const share = target > 0 ? Math.min(1, current / target) : 0;
  return (
    <div className="macro-row">
      <span className="type-caption-13 muted">{label}</span>
      <span className="type-caption-13 faint tnum">
        {current} / {target} {t('today.gramsShort')}
      </span>
      <div className="macro-track">
        <div
          className="macro-fill"
          style={{ width: `${share * 100}%`, background: `var(${colorVar})` }}
        />
      </div>
    </div>
  );
}

export function TodayScreen(): JSX.Element {
  const { t, i18n } = useTranslation();
  const numberLocale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  return (
    <main className="screen">
      <h1 className="type-h1 screen-title">{t('today.title')}</h1>

      <section className="card dayring-card">
        {/* Смысл «осталось» — подписью карточки, СНАРУЖИ кольца:
            внутри кольца только цифры и «ккал» (бренд-правило). */}
        <p className="type-micro-11 card-label" style={{ alignSelf: 'flex-start', margin: 0 }}>
          {t('today.ringTitle')}
        </p>
        <DayRing consumedKcal={MOCK.consumed.kcal} targetKcal={MOCK.plan.kcal} />
        <div className="macros">
          <MacroRow
            label={t('today.protein')}
            current={MOCK.consumed.proteinG}
            target={MOCK.plan.proteinG}
            colorVar="--macro-p"
          />
          <MacroRow
            label={t('today.fat')}
            current={MOCK.consumed.fatG}
            target={MOCK.plan.fatG}
            colorVar="--macro-f"
          />
          <MacroRow
            label={t('today.carbs')}
            current={MOCK.consumed.carbsG}
            target={MOCK.plan.carbsG}
            colorVar="--macro-c"
          />
        </div>
      </section>

      <section className="card">
        <p className="type-micro-11 card-label">{t('today.activity')}</p>
        <div className="list-row">
          <div>
            <p className="type-body-15" style={{ margin: 0 }}>
              {t(MOCK.workout.titleKey)} · {MOCK.workout.durationMin} {t('today.minShort')}
            </p>
            <p className="type-caption-13 faint tnum" style={{ margin: 0 }}>
              {MOCK.workout.kcal} {t('today.kcalUnit')} · {t('today.burnedNote')}
            </p>
          </div>
        </div>
        <div className="list-row">
          <span className="type-body-15 muted">{t('today.steps')}</span>
          <span className="type-caption-13 faint tnum">
            {MOCK.steps.toLocaleString(numberLocale)}
          </span>
        </div>
      </section>

      <div className="row-2col">
        <section className="card">
          <p className="type-micro-11 card-label">{t('today.weight')}</p>
          <span className="type-display-44 tnum">{MOCK.weightKg}</span>
          <p className="type-caption-13 faint tnum" style={{ margin: 'var(--space-4) 0 0' }}>
            {t('today.weightCaption', { delta: MOCK.weightDelta })}
          </p>
        </section>
        <section className="card">
          <p className="type-micro-11 card-label">{t('today.habits')}</p>
          <div className="chips">
            {MOCK.habits.map((h) => (
              <span key={h.nameKey} className={h.done ? 'chip done' : 'chip'}>
                <span className="type-caption-13">{t(h.nameKey)}</span>
                <span className="type-caption-13 tnum">
                  {'valueKey' in h ? t(h.valueKey) : h.value}
                </span>
              </span>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
